import { Button } from "@/components/ui/button";
import { Check, Users, GraduationCap, Heart, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import Page from "@/components/layout/Page";

const useCases = [
  {
    icon: GraduationCap,
    title: "Schools & Education",
    description: "Support students with sensory needs, ADHD, and autism in the classroom. Help create calm, focused learning environments."
  },
  {
    icon: Heart,
    title: "Therapists & Clinics",
    description: "Occupational therapists, psychologists, and mental health professionals use Samboards as therapeutic tools for sensory integration and stress management."
  },
  {
    icon: Building2,
    title: "NHS & Healthcare",
    description: "Trusted by NHS teams for dementia care, stroke recovery, and mental health support. Evidence-based sensory solutions."
  },
  {
    icon: Users,
    title: "Care Homes",
    description: "Provide meaningful engagement for residents with dementia, Alzheimer's, and other conditions requiring sensory stimulation."
  }
];

const benefits = [
  "Bulk pricing available",
  "Dedicated account management",
  "Custom product consultations",
  "Fast delivery for institutions",
  "Ongoing professional support",
  "Training and guidance materials"
];

export default function Professionals() {
  return (
    <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
      <Page>
        <section className="w-full pb-24">
          <div className="max-w-[1000px] mx-auto px-6 text-center">
          <h1 className="text-[54px] leading-[1.1] font-bold mb-6">
            For Schools, Therapists & Healthcare Professionals
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-[700px] mx-auto mb-8">
            Trusted by NHS teams, therapists, schools, and care facilities across the UK. 
            Premium sensory solutions for professional use.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-[10px] shadow-md">
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </div>
      </section>

      {/* Use Cases */}
      <section className="w-full py-24">
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-[40px] font-bold mb-12 text-center">Who We Work With</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="card-surface texture-stone p-8">
                <useCase.icon className="w-12 h-12 text-primary mb-4" strokeWidth={1.5} />
                <h3 className="text-2xl font-semibold mb-3">{useCase.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="w-full py-24 bg-card/30">
        <div className="max-w-[800px] mx-auto px-6">
          <h2 className="text-[40px] font-bold mb-12 text-center">Professional Partnership Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 p-4">
                <Check className="w-6 h-6 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-base font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="w-full py-24">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="panel texture-fiber text-center space-y-6">
            <h2 className="text-[40px] font-bold">Ready to Partner With Us?</h2>
            <p className="text-lg text-muted-foreground">
              Whether you're a school, therapist, or healthcare facility, we're here to support 
              your sensory needs with quality products and dedicated service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary-hover rounded-[10px] shadow-md">
                <Link to="/contact">Contact us</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-foreground rounded-[10px]">
                <Link to="/samboards">View products</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      </Page>
    </div>
  );
}
